import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { space, tokPx } from '@sunwebgroup/ui-components'
import { focusOutline, Icon, IconGlyph, ListItem, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

interface MealplansItemProps {
  title: string
  description?: string | null
  mealplanCode: string
}

const IconChevron = styled(Icon)`
  margin-left: auto;
`
const ButtonWrapper = styled.button`
  ${focusOutline()}
  border-radius: ${(tokPx('primitives_corner_radius_m'))};
`
const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_8')};
`
const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
function getIcon(mealplanCode: string): IconGlyph {
  switch (mealplanCode) {
    case 'LG':
      // Stay only
      return 'ServingFoodWithClocheSlashed'
    case 'LO':
      // Breakfast only
      return 'BowlWithFood'
    case 'HP':
      // Half Pension
      return 'ServingFoodWithHalfCloche'
    case 'VP':
      // Full Pension
      return 'ServingFoodWithFullCloche'
    case 'AI':
      // All Inclusive
      return 'ServingFoodWithPlusCloche'
    case 'UA':
      // Ultra All Inclusive
      return 'ServingFoodWithStarCloche'

    default:
      return 'ServingFoodWithCloche'
  }
}

export function MealplansItem({ title, description, mealplanCode }: MealplansItemProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const [open, setOpen] = React.useState(false)
  const subtitleId = title.toLowerCase().replace(/[\- /]/g, '_')
  const subTitle = intl.formatMessage({ id: 'accommodation_page_mealplan_item_subtitle' }, { id: subtitleId })
  const iconGlyph = getIcon(mealplanCode)

  function handleReadMoreOpenChange(itemLabel: string) {
    setOpen(!open)
    if (!open) {
      sendEvent(
        createPDPClickEvent({
          element: 'Read More',
          description: `Mealplan info`,
          result: itemLabel.toLowerCase(),
        }),
      )
    }
  }

  if (!description) {
    return (
      <ListItem variant="outlined">
        <ItemRow>
          <Icon glyph={iconGlyph} size={32} />
          <LabelWrapper>
            <Typography $variant="body-l-bold-tight">{title}</Typography>
            <Typography $variant="body-m-regular-tight">{subTitle}</Typography>
          </LabelWrapper>
        </ItemRow>
      </ListItem>
    )
  }

  return (
    <Sheet.Root open={open} onOpenChange={() => handleReadMoreOpenChange(title)}>
      <Sheet.Trigger>
        <ButtonWrapper>
          <ListItem variant="outlined">
            <ItemRow>
              <Icon glyph={iconGlyph} size={32} />
              <LabelWrapper>
                <Typography $variant="body-l-bold-tight">{title}</Typography>
                <Typography $variant="body-m-regular-tight">{subTitle}</Typography>
              </LabelWrapper>
              <IconChevron glyph="ChevronRight" size={20} />
            </ItemRow>
          </ListItem>
        </ButtonWrapper>
      </Sheet.Trigger>
      <Sheet.Body side="right">
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={title}
        />
        <Sheet.Content>
          <Typography dangerouslySetInnerHTML={{ __html: description ?? '' }} />
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  )
}
